import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateFeeStatusLookupListFormValues } from '../../../../../interfaces/lookup/feeStatus';
import { IUpdateFeeStatus } from '../../../../../interfaces/lookup/feeStatus/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchFeeStatusById, updateFeeStatus } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const FeeStatusLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateFeeStatusLookupListFormValues) => {
        const body: IUpdateFeeStatus = {
            id: lookupSlice.detailsPage.feeStatus.data?.id,
            ...(values as any),
        };

        dispatch(updateFeeStatus(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.feeStatus.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/fee-status', { replace: true });
        }
    }, [lookupSlice.detailsPage.feeStatus.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchFeeStatusById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.feeStatus.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.feeStatus.data.id,
                code: lookupSlice.detailsPage.feeStatus.data.code,
                description: lookupSlice.detailsPage.feeStatus.data.description,
            } as IUpdateFeeStatusLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.feeStatus.data]);

    if (lookupSlice.detailsPage.feeStatus.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="feestatus-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage fee statuses lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Fee statuses',
                        path: '/admin/lookup-lists/fee-status',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.feeStatus.data?.description || '-',
                        path: `/admin/lookup-lists/fee-status/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/fee-status/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.feeStatus.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.feeStatus.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.feeStatus.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.feeStatus.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.feeStatus.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.feeStatus.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.feeStatus.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.feeStatus.fetchStatus} about={'Fee status'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Fee status Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <p>{lookupSlice.detailsPage.feeStatus.data?.code || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.feeStatus?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.feeStatus.data?.created
                                            ? dayjs(lookupSlice.detailsPage.feeStatus.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.feeStatus.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.feeStatus.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.feeStatus.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
